import {
  BarList,
  ReportSection,
  ReportTable,
  Stat,
  StatGrid,
} from "../ReportPrimitives";
import type { MarketingReport } from "../reportTables";

const conversion = (customers: number, contacts: number) =>
  contacts === 0 ? ", " : `${Math.round((customers / contacts) * 100)}%`;

const prettyStage = (stage: string) =>
  stage.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * Marketing: where leads come from and what the outreach actually did.
 * Campaign outcomes are read from the recipient ledger, so a consent-suppressed
 * send is shown as its own column rather than hidden inside "delivered".
 */
export const MarketingPanel = ({ data }: { data: MarketingReport }) => (
  <div className="flex flex-col gap-4">
    <StatGrid>
      <Stat
        label="New contacts"
        value={String(data.new_contacts)}
        helper="Added in this period"
      />
      <Stat
        label="Form submissions"
        value={String(data.form_submissions)}
        helper="Across all forms"
      />
      <Stat
        label="Campaigns sent"
        value={String(data.campaigns_sent)}
        helper="Delivered in this period"
      />
      <Stat
        label="Lead → customer"
        value={conversion(
          data.by_source.reduce((sum, row) => sum + row.customers, 0),
          data.by_source.reduce((sum, row) => sum + row.contacts, 0),
        )}
        helper="Of contacts added in period"
      />
    </StatGrid>

    <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <ReportSection
        title="Leads by source"
        subtitle="And how many became customers"
      >
        <ReportTable
          columns={["Source", "Contacts", "Customers", "Rate"]}
          align={["left", "right", "right", "right"]}
          rows={data.by_source.map((row) => [
            row.source,
            row.contacts,
            row.customers,
            conversion(row.customers, row.contacts),
          ])}
          empty="No contacts added in this period."
        />
      </ReportSection>

      <ReportSection
        title="Contacts by lifecycle"
        subtitle="Whole database, not just this period"
      >
        <BarList
          items={data.by_lifecycle.map((row) => ({
            label: prettyStage(row.stage),
            value: row.count,
            display: String(row.count),
          }))}
          empty="No contacts yet."
        />
      </ReportSection>
    </div>

    <ReportSection
      title="Campaign performance"
      subtitle="From the recipient ledger"
    >
      <ReportTable
        columns={[
          "Campaign",
          "Channel",
          "Recipients",
          "Delivered",
          "Suppressed",
          "Failed",
        ]}
        align={["left", "left", "right", "right", "right", "right"]}
        rows={data.campaigns.map((row) => [
          row.name,
          row.channel.toUpperCase(),
          row.recipients,
          row.delivered,
          row.suppressed,
          row.failed,
        ])}
        empty="No campaigns in this period."
      />
    </ReportSection>

    <ReportSection
      title="Forms"
      subtitle="Submissions and the deals they created"
    >
      <ReportTable
        columns={["Form", "Submissions", "Deals"]}
        align={["left", "right", "right"]}
        rows={data.by_form.map((row) => [row.name, row.submissions, row.deals])}
        empty="No form submissions in this period."
      />
    </ReportSection>
  </div>
);
